import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { createApiClient } from 'lib/api/createApiClient';
import { useAuthStore } from 'lib/stores/authStore';
import { getNonwithdrawableCents, type HistoricalPortfolioVo, type NonwithdrawableCredit } from 'models/portfolio';
import { useSubAccountStore } from 'stores/zustand/subAccountStore';
import config from 'utils/config';

interface GetUserPortfolioCurrentResponse {
    value: HistoricalPortfolioVo;
}

export const PORTFOLIO_CURRENT_POLL_INTERVAL_MS = 5_000;

const api = createApiClient({
    baseURL: `${config.exchangeApiUrl}/v1`,
});

export const portfolioCurrentKeys = {
    all: ['portfolioCurrent'] as const,
    user: (userId: string, subaccountNumber: number) =>
        [...portfolioCurrentKeys.all, userId, subaccountNumber] as const,
};

const getPortfolioCurrent = async (
    userId: string,
    subaccountNumber: number,
    signal?: AbortSignal,
): Promise<HistoricalPortfolioVo> => {
    const response = await api.get<GetUserPortfolioCurrentResponse>(`/users/${userId}/portfolio/current_value`, {
        params: { subaccount_number: subaccountNumber },
        signal,
    });
    return response.data.value;
};

export const usePortfolioCurrent = <TData = HistoricalPortfolioVo>(
    options?: Omit<UseQueryOptions<HistoricalPortfolioVo, Error, TData>, 'queryKey' | 'queryFn'>,
) => {
    const userId = useAuthStore((s) => s.userId);
    const isMember = useAuthStore((s) => s.isLoggedInMember);
    const subaccountNumber = useSubAccountStore((s) => s.subaccountNumber);
    return useQuery({
        queryKey: portfolioCurrentKeys.user(userId ?? '', subaccountNumber),
        queryFn: ({ signal }) => getPortfolioCurrent(userId ?? '', subaccountNumber, signal),
        // Mutations invalidate this key immediately; the interval is only the fallback for
        // out-of-band balance changes, so polling faster than 5s adds load without freshness.
        refetchInterval: PORTFOLIO_CURRENT_POLL_INTERVAL_MS,
        enabled: !!userId && isMember,
        ...options,
    });
};

export const useCashInDollars = (): number => {
    const { data } = usePortfolioCurrent({
        select: (portfolio) => {
            if (isNaN(portfolio.a)) return 0;
            return portfolio.a / 100;
        },
    });
    return data ?? 0;
};

export const useBonusInDollars = (): number => {
    const { data } = usePortfolioCurrent({
        select: (portfolio) => getNonwithdrawableCents(portfolio) / 100,
    });
    return data ?? 0;
};

export const useHasCredits = (): boolean => {
    const { data } = usePortfolioCurrent({
        select: (portfolio) =>
            (portfolio.nonwithdrawable_credits ?? []).length > 0 || getNonwithdrawableCents(portfolio) > 0,
    });
    return data ?? false;
};

export const useWithdrawableCashInDollars = (): number => {
    const { data } = usePortfolioCurrent({
        select: (portfolio) => {
            const cash = isNaN(portfolio.a) ? 0 : portfolio.a / 100;
            const bonus = getNonwithdrawableCents(portfolio) / 100;
            return Math.max(0, cash - bonus);
        },
    });
    return data ?? 0;
};

export const useInvestedValueInDollars = (): number => {
    const { data } = usePortfolioCurrent({
        select: (portfolio) => (isNaN(portfolio.v) ? 0 : portfolio.v / 100),
    });
    return data ?? 0;
};

export const usePortfolioTotalValue = (): { totalValue: number; isLoaded: boolean } => {
    const { data, isFetched } = usePortfolioCurrent({
        select: (portfolio) => {
            const cash = isNaN(portfolio.a) ? 0 : portfolio.a / 100;
            const invested = isNaN(portfolio.v) ? 0 : portfolio.v / 100;
            return cash + invested;
        },
    });
    return { totalValue: data ?? 0, isLoaded: isFetched };
};

export const usePendingValueInDollars = (): number => {
    const { data } = usePortfolioCurrent({
        select: (portfolio) => (isNaN(portfolio.pending_v) ? 0 : portfolio.pending_v / 100),
    });
    return data ?? 0;
};

export const useUnrealizedPnlInDollars = (): number => {
    const { data } = usePortfolioCurrent({
        select: (portfolio) => (portfolio.unrealized_pnl ?? 0) / 100,
    });
    return data ?? 0;
};

export const useUnrealizedPnlInPercent = (): number => {
    const { data } = usePortfolioCurrent({
        select: (portfolio) => {
            const invested = isNaN(portfolio.v) ? 0 : portfolio.v / 100;
            const unrealized = (portfolio.unrealized_pnl ?? 0) / 100;
            const costBasis = invested - unrealized;
            if (costBasis === 0) return 0;
            return unrealized / costBasis;
        },
    });
    return data ?? 0;
};

export const useNonwithdrawableCredits = (): NonwithdrawableCredit[] => {
    const { data } = usePortfolioCurrent({
        select: (portfolio) => portfolio.nonwithdrawable_credits ?? [],
    });
    return data ?? [];
};

export const useIsWhale = (): { isWhale: boolean; isLoaded: boolean } => {
    const { data, isFetched } = usePortfolioCurrent({
        select: (portfolio) => {
            const cash = isNaN(portfolio.a) ? 0 : portfolio.a / 100;
            const invested = isNaN(portfolio.v) ? 0 : portfolio.v / 100;
            return cash + invested >= 10000;
        },
    });
    return { isWhale: data ?? false, isLoaded: isFetched };
};
